import React from "react";
import { Button, InputNumber, Popover } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

/**
 * Reusable popover component for editing stock values with delta input
 * Shows current value, change (delta), and calculated new value
 */
const StockEditPopover = ({
  visible,
  onVisibleChange,
  currentValue,
  editValue,
  onEditValueChange,
  onSave,
  title,
  loading = false,
  children,
}) => {
  const { t } = useTranslation();

  const delta = Number(editValue || 0);
  const current = Number(currentValue || 0);
  const newValue = current + delta;

  const deltaColor = delta > 0 ? "#52c41a" : delta < 0 ? "#f5222d" : "#888";
  const deltaLabel = `${delta >= 0 ? "+" : ""}${isNaN(delta) ? 0 : delta}`;

  const popoverContent = (
    <div style={{ padding: "8px 0" }}>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{t("enter_difference_to_adjust")}</div>
        <InputNumber
          value={editValue}
          onChange={onEditValueChange}
          style={{ width: "100%" }}
          disabled={loading}
          autoFocus
          onPressEnter={onSave}
          formatter={(v) => {
            if (v === undefined || v === null || v === "") return "";
            const n = Number(String(v).replace("+", ""));
            if (isNaN(n)) return String(v);
            return `${n >= 0 ? "+" : ""}${n}`;
          }}
          parser={(v) => {
            if (v === undefined || v === null) return 0;
            const s = String(v).replace("+", "");
            const n = Number(s);
            return isNaN(n) ? 0 : n;
          }}
        />
      </div>
      <div style={{ marginBottom: 8, fontSize: 12 }}>
        <div>
          <span style={{ color: "#666" }}>{t("current")}: </span>
          <strong>{current}</strong>
        </div>
        <div>
          <span style={{ color: "#666" }}>{t("change")}: </span>
          <strong style={{ color: deltaColor }}>{deltaLabel}</strong>
        </div>
        <div>
          <span style={{ color: "#666" }}>{t("new_value")}: </span>
          <strong>{isNaN(newValue) ? current : newValue}</strong>
        </div>
      </div>
      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={onSave}
        size="small"
        block
        loading={loading}
        disabled={loading}
      >
        {t("save")}
      </Button>
    </div>
  );

  return (
    <Popover content={popoverContent} title={title} trigger="click" open={visible} onOpenChange={onVisibleChange}>
      {children}
    </Popover>
  );
};

StockEditPopover.propTypes = {
  visible: PropTypes.bool.isRequired,
  onVisibleChange: PropTypes.func.isRequired,
  currentValue: PropTypes.number.isRequired,
  editValue: PropTypes.number.isRequired,
  onEditValueChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export default StockEditPopover;
